import { addBreadcrumb, captureException } from '@sentry/react'
import { isPresent } from 'ts-extras'
import { call, delay, put, select, takeLatest } from 'typed-redux-saga'
import { Action, Actions, ActionTypes, AnyAction } from '~/actions'
import {
	CartAddDocument,
	CartCreateDocument,
	CartLoadDocument,
	CartLoadUpsellsDocument,
	CartRemoveDocument,
	CartUpdateDocument
} from '~/gql'
import type { CartAddMutation, CartLineInput, Checkout, GQLResponse, State } from '~/types'
import {
	CartError,
	ENV,
	extractConnections,
	FBEvent,
	FilterActions,
	getCurrentCountry,
	getPrice,
	gidToId,
	isError,
	isOk,
	shopifyFetch,
	sleep,
	toGraphId,
	track
} from '~/utils'
import { initialState, LoadedCart } from '../reducers/cart'

const updateActions: ActionTypes[] = ['CART_ADD', 'CART_QUANTITY_UPDATE', 'CART_REMOVE']

export function* cartSagas() {
	yield* takeLatest('CART_FETCH', loadCart)
	yield* takeLatest('CART_FETCH', loadUpsells)
	yield* takeLatest('CART_FETCHED', onCartFetched)
	yield* takeLatest(updateActions, updateCart)
	yield* takeLatest((a: AnyAction) => a.type.startsWith('CART_'), saveState)
}

export function* saveState(action: FilterActions<Action, 'CART'>) {
	const cart = yield* select((s: State) => s.cart)
	const excludedActions: Array<ActionTypes<'CART_'>> = ['CART_OPEN', 'CART_FETCH', 'CART_FETCHED']

	if (cart.loaded && !excludedActions.includes(action.type))
		localStorage.setItem('cart', JSON.stringify({ ...cart, ...initialState }))
}

function* onCartFetched(action: Action<'CART_FETCHED'>) {
	yield put(Actions.cartLoaded(action.payload))
	// Remove unpurchasable / removed items from cart
	const lineItemsToRemove = action.payload.lines.filter(item => {
		if (item.merchandise == null) return true // Happens if a product is removed / went from 1 to multiple variants
		return false
	})

	for (const item of lineItemsToRemove) {
		yield put(Actions.removeFromCart(item.id))
	}

	yield delay(3000)
	addBreadcrumb({
		category: 'checkout',
		message: `Loaded checkout ${action.payload.id}`,
		level: 'info',
		data: action.payload
	})
}

function* loadUpsells(_action: Action<'CART_FETCH'>) {
	const upsellQuery = yield* call(() => shopifyFetch(CartLoadUpsellsDocument, {}))
	if (!upsellQuery.errors && upsellQuery.data?.items) {
		const upsells = upsellQuery.data.items.references
			?.map(extractConnections)
			.map(metaobject =>
				metaobject.item?.reference
					? {
							item: metaobject.item?.reference,
							expiry: metaobject.expiry?.value ? new Date(metaobject.expiry.value) : null,
							expiryText: metaobject.expiry_text?.value ?? null,
							offer: metaobject.offer?.value ?? undefined
					  }
					: null
			)
			.filter(isPresent)

		if (upsells) yield* put(Actions.cartUpsellsLoaded(upsells))
	}
}

function* loadCart(_action: Action<'CART_FETCH'>) {
	let attempts = 0
	let error: Error | null = null
	while (++attempts <= 10) {
		try {
			const cart = JSON.parse(localStorage.getItem('cart') as string) as LoadedCart | null
			if (cart?.id) {
				if ('lines' in cart) {
					// Put local state into view if cart is of the latest version
					yield* put(Actions.cartFetched(cart))
				}

				const res = yield* call(() => shopifyFetch(CartLoadDocument, { id: cart.id }))

				if (isError(res)) {
					// Invalid token, clear cart
					if (res.errors.some(e => e.message.toLowerCase().includes('invalid'))) {
						localStorage.removeItem('cart')
					} else {
						console.warn('Could not load cart', res.errors)
					}
					continue
				}
				const checkout = res.data
				console.debug('Checkout loaded', checkout)
				// Check if cart is not completed (null means completed cart)
				if (checkout !== null) {
					console.debug('Checkout loaded', checkout)

					// Make sure ?key= parameter is included
					if (cart.id.includes(checkout.id)) checkout.id = cart.id

					return yield* put(Actions.cartFetched(checkout))
				} else {
					// Discard cart as a completed cart cant be modified
					localStorage.removeItem('cart')
					continue // Retry
				}
			} else {
				const res = yield* call(() => shopifyFetch(CartCreateDocument, {}))
				if (isError(res)) {
					captureException(res.errors[0])
					console.error(res.errors)
					continue // Retry
				} else if (res.data) {
					console.debug('Checkout created', res.data)
					return yield* put(Actions.cartFetched(res.data))
				}
			}
		} catch (e) {
			error = e as Error
		}
		yield* call(sleep, Math.pow(attempts, 2) * 500) // Exponential backoff
	}
	if (error) {
		const cart = localStorage.getItem('cart')
		console.warn('Failed to create checkout', error)
		if (error.name) error.name = 'CartCreateError'
		captureException(error, {
			extra: {
				cart: cart ? JSON.parse(cart) : null
			}
		})
	}
}

function* updateCart(action: FilterActions<Action, 'CART'>) {
	const cartId = yield* select((s: State) => s.cart.id)
	if (!cartId) return null

	const prevState = action['@CONTEXT']?.prevState as Checkout
	let result: CartResponse<CartAddMutation>
	switch (action.type) {
		case 'CART_ADD':
			result = yield* call(addToCart, action, cartId)
			yield* put(Actions.openCart(true))
			if (ENV().type === 'production' && isOk(result.res)) {
				const params: facebook.Pixel.ViewContentParameters = {
					contents: action.payload.map(item => ({
						...item,
						id: gidToId(item.id).toString()
					})),
					currency: getCurrentCountry().currency.iso_code,
					value: result.res.data?.lines.reduce((a, b) => {
						const quantity = action.payload.find(item => item.id === b.id)?.quantity ?? 0
						return a + quantity * getPrice(b.merchandise?.price)
					}, 0)
				}
				track(FBEvent.AddToCart, params)
			}
			break
		case 'CART_REMOVE':
			result = yield* call(removeFromCart, action, cartId)
			break
		case 'CART_QUANTITY_UPDATE':
			result = yield* call(updateQuantity, action, cartId)
			break
		default:
			return
	}

	if (isOk(result.res)) {
		yield* put(Actions.updateCartSucess(result.res.data))
	} else if (result.message) {
		yield* put(Actions.updateCartError(result.res.data ?? prevState, [new CartError(result.message, result.res)]))
	}
}

async function removeFromCart(action: Action<'CART_REMOVE'>, cartId: CartID) {
	const lines = [toGraphId(action.meta, 'variant')]
	const res = await shopifyFetch(CartRemoveDocument, { id: cartId, lines })
	return cartResponse(res, 'Error removing from cart')
}

async function addToCart(action: Action<'CART_ADD'>, cartId: CartID) {
	const lineItemsToAdd = action.payload.map<CartLineInput>(p => ({
		quantity: p.quantity,
		merchandiseId: toGraphId(p.id, 'variant')
	}))
	const res = await shopifyFetch(CartAddDocument, { id: cartId, lines: lineItemsToAdd })
	return cartResponse(res, 'Error adding to cart')
}

async function updateQuantity(action: Action<'CART_QUANTITY_UPDATE'>, cartId: CartID) {
	const lineItemsToUpdate = [{ id: toGraphId(action.meta, 'variant'), quantity: action.payload }]
	const res = await shopifyFetch(CartUpdateDocument, { id: cartId, lines: lineItemsToUpdate })
	return cartResponse(res, 'Error updating quantity')
}

interface CartResponse<T> {
	res: GQLResponse<T>
	message: string | null
}

function cartResponse<T>(res: GQLResponse<T>, message: string): CartResponse<T> {
	return {
		res,
		message: isOk(res) ? null : message
	}
}
